import { Component, inject, Input, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Record } from 'src/app/models/record';
import { ApiService } from 'src/app/services/api.service';
import { SliderComponent } from '../slider/slider.component';
import { PaginatedFavorites } from 'src/app/state/user/favorites/models/favorites.response';
import { FavoritesFacade } from 'src/app/state/user/favorites/favorites.facade';
import { filter, Subscription } from 'rxjs';

@Component({
  selector: 'app-slider-favourites',
  templateUrl: './slider-favourites.component.html',
  styleUrls: ['./slider-favourites.component.scss'],
})
export class SliderFavouritesComponent implements OnInit, OnDestroy {
  @Input()
  paginatedFavorites: PaginatedFavorites;

  @ViewChild('slider')
  slider: SliderComponent;

  records: Record[] = [];
  loading: boolean;
  showEmptyBoard: boolean;

  private readonly favoriteFacade = inject(FavoritesFacade);
  private favoriteAddedSubscription: Subscription;
  private favoriteRemovedSubscription: Subscription;

  readonly showDeleteAllFavoritesDialog = signal<boolean>(false);

  constructor(
    private apiService: ApiService,
    private router: Router,
  ) { }

  ngOnInit() {
    this.loadRecords();

    this.favoriteAddedSubscription = this.favoriteFacade.favoriteAdded$.pipe(
      filter(favoriteAdded => !!favoriteAdded)).subscribe((favoriteAdded) => {
        this.loadRecord(favoriteAdded.recordId);
      });

    this.favoriteRemovedSubscription = this.favoriteFacade.favoriteRemoved$.pipe(
      filter(favoriteRemoved => !!favoriteRemoved)).subscribe((favoriteRemoved) => {
        const removedItemIndex = this.records.findIndex((r) => r.id === favoriteRemoved.recordId);
        if (removedItemIndex !== -1) {
          this.records.splice(removedItemIndex, 1);
          this.slider.removeItemByIndex(removedItemIndex);
          this.shouldShowEmptyBoard();
        }
      });
  }

  ngOnDestroy(): void {
    this.favoriteAddedSubscription?.unsubscribe();
    this.favoriteRemovedSubscription?.unsubscribe();
  }

  shouldShowEmptyBoard() {
    this.showEmptyBoard = this.records.length === 0;
  }

  goToFavourites() {
    this.router.navigate(['/favourites']);
  }

  removeAllFavorites() {
    this.showDeleteAllFavoritesDialog.set(false);
    this.favoriteFacade.deleteAllFavorites().subscribe(() => {
      this.records = [];
      this.slider.deleteSwiperItems();
      this.shouldShowEmptyBoard();
    });
  }

  exploreCatalog() {
    this.slider.exploreCatalog();
  }

  private loadRecords() {
    this.loading = true;
    const recordIds = this.paginatedFavorites?.items.map((fav) => fav.recordId).slice(0, 10);
    if (recordIds?.length > 0) {
      this.apiService.getRecords({ recordIds: recordIds }).subscribe((result) => {
        this.records.push(...result.records);
        this.loading = false;
        this.shouldShowEmptyBoard();
      });
    } else {
      this.loading = false;
      this.shouldShowEmptyBoard();
    }
  }

  private loadRecord(recordId: string) {
    this.apiService.getRecords({ recordIds: recordId }).subscribe((result) => {
      this.records.unshift(...result.records);
      this.slider.refreshSwiperItems();
      this.shouldShowEmptyBoard();
    });
  }

  addTitles() {
    this.router.navigate(['/search', 'lists', 'favorites']);
  }
}
